import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { Wizard } from "@/components/ui/wizard";
import { ConfirmDialog } from "@/components/ui/dialog/confirm-dialog";
import { toast } from "@/components/ui/toast";
import { PROTECTED_ROUTES } from "@/utils/navigation";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";
import { useAutoCollapsedSidebar } from "@/hooks/use-auto-collapsed-sidebar";
import { getApiErrorMessages } from "@/utils/api-error";
import {
  StaffMemberCreationSchema,
  type StaffMemberCreationFormData,
} from "@/schemas/staff-member";
import { useStaffWizardSteps } from "@/components/create-staff-member/use-staff-wizard-steps";
import { staffMemberFormToCreatePayload } from "@/components/create-staff-member/staff-member-payload";
import { EMPLOYMENT_TYPE } from "@/constants/staff";
import { ID_DOCUMENT_TYPE } from "@/constants/athlete";
import { useCreateStaffMember } from "@/queries/use-staff-members";
import { useGyms } from "@/queries/use-gyms";

export const Route = createFileRoute("/(protected)/staff/new/")({
  component: RouteComponent,
  // Each entry point seeds the first gym assignment with what it already knows:
  // the staff list of a gym passes that gym, a role-scoped page passes that role.
  // Keeping it in the URL means a new entry point only has to navigate with it.
  validateSearch: (search: Record<string, unknown>) => ({
    gymId: search.gymId !== undefined ? String(search.gymId) : undefined,
    roleId: search.roleId !== undefined ? String(search.roleId) : undefined,
  }),
});

const DEFAULT_VALUES: StaffMemberCreationFormData = {
  avatar: "",
  firstName: "",
  lastName: "",
  email: "",
  birthdate: "",
  phoneNumber: "",
  landlineNumber: "",
  gender: "",
  maritalStatus: "",
  nationality: "",
  idDocumentType: ID_DOCUMENT_TYPE.CitizenCard,
  idDocumentNumber: "",
  idDocumentExpirationDate: "",
  emergencyContactName: "",
  emergencyContactPhoneNumber: "",
  emergencyContactRelationship: "",
  medicalConditions: "",
  allergiesIntolerances: "",
  currentMedication: "",
  disciplineIds: [],
  employmentType: EMPLOYMENT_TYPE.Contract,
  gymAssignments: [],
  primaryAssignmentIndex: 0,
  addressLine1: "",
  addressLine2: "",
  city: "",
  postalCode: "",
  fiscalFullName: "",
  taxId: "",
  contractStartDate: "",
  contractEndDate: "",
  iban: "",
  billingAddressSameAsMain: true,
  billingAddressLine1: "",
  billingAddressLine2: "",
  billingCity: "",
  billingPostalCode: "",
};

function RouteComponent() {
  useAutoCollapsedSidebar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { gymId, roleId } = Route.useSearch();
  const { mutate: createStaffMember, isPending } = useCreateStaffMember();
  const { data: gymsData } = useGyms();

  const form = useForm<StaffMemberCreationFormData>({
    resolver: zodResolver(StaffMemberCreationSchema()),
    mode: "onSubmit",
    defaultValues: {
      ...DEFAULT_VALUES,
      gymAssignments: [{ gymId: gymId ?? "", roleIds: roleId ? [roleId] : [] }],
    },
  });

  const steps = useStaffWizardSteps();

  const navigateBack = () => {
    navigate({ to: PROTECTED_ROUTES.STAFF });
  };

  const { handleCancelEdit, unsavedChangesDialogProps } = useUnsavedChanges({
    isEditing: true,
    isDirty: form.formState.isDirty,
    onLeave: navigateBack,
  });

  const handleComplete = (data: StaffMemberCreationFormData) => {
    const payload = staffMemberFormToCreatePayload(data);
    const primaryGymId = payload.staffMember.defaultGym.id;

    createStaffMember(payload, {
      onSuccess: () => {
        const primaryGymName = (gymsData?.gyms ?? []).find(
          (gym) => gym.id === primaryGymId,
        )?.name;

        toast.success(t("pages:staff.wizard.toaster.success.title"), {
          description: t("pages:staff.wizard.toaster.success.description", {
            gym: primaryGymName,
          }),
        });
        navigateBack();
      },
      onError: (error) => {
        toast.error(t("pages:staff.wizard.toaster.error.title"), {
          description: getApiErrorMessages(error),
        });
      },
    });
  };

  const handleCancel = () => {
    handleCancelEdit(navigateBack);
  };

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>{t("pages:staff.wizard.title")}</MobilePageTitle>
      <PageContainer
        className="flex min-h-0 min-w-[309px] flex-1 flex-col p-0 md:m-0 lg:mb-0"
        internalScroll
      >
        <Wizard
          form={form}
          steps={steps}
          onComplete={handleComplete}
          isSubmitting={isPending}
          onCancel={handleCancel}
        />
      </PageContainer>
      <ConfirmDialog {...unsavedChangesDialogProps} />
    </div>
  );
}
